import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import { Disclose, EmptyState, Loading, Permission, Refusal, kernel } from "@lopie/kernel-ui";

/**
 * Cash Flow · 30 days — money in and money out, per day, from Payment records.
 *
 * The collection path and the field names are checked against GET /schema at
 * load, so a renamed field is said on screen rather than drawn as zeros.
 * A refusal from the kernel is rendered with the kit's Refusal (the app was
 * never allowed to ask); zero payments from a request that succeeded is a
 * different sentence (this person can see none in the window).
 */

const DAYS = 30;
const PAGE = 200;
const MAX_PAGES = 100;
const ACTOR = "Cash Flow · 30 days";
const NEEDED = ["direction", "amount", "currency", "paid_at"];

function dayKey(d) {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${dd}`;
}

function windowDays(now) {
  const base = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const out = [];
  for (let i = DAYS - 1; i >= 0; i -= 1) {
    const d = new Date(base);
    d.setDate(base.getDate() - i);
    out.push(d);
  }
  return out;
}

function money(minor, currency) {
  try {
    const f = new Intl.NumberFormat(undefined, { style: "currency", currency });
    const exp = f.resolvedOptions().maximumFractionDigits;
    return f.format(minor / 10 ** exp);
  } catch {
    return `${minor} ${currency} (minor units)`;
  }
}

function shortDate(d) {
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function summarise(items, now) {
  const days = windowDays(now);
  const keys = days.map(dayKey);
  const inWindow = new Set(keys);
  const byCurrency = {};
  let unreadable = 0;
  let held = 0;
  let outside = 0;

  for (const item of items) {
    const f = item.fields || {};
    const when = new Date(f.paid_at);
    const dir = f.direction;
    const amt = f.amount;
    if (
      Number.isNaN(when.getTime())
      || (dir !== "inbound" && dir !== "outbound")
      || !Number.isInteger(amt)
      || typeof f.currency !== "string"
    ) {
      unreadable += 1;
      continue;
    }
    const key = dayKey(when);
    if (!inWindow.has(key)) {
      outside += 1;
      continue;
    }
    const cur = f.currency.toUpperCase();
    if (!byCurrency[cur]) {
      byCurrency[cur] = {
        in: Object.fromEntries(keys.map((k) => [k, 0])),
        out: Object.fromEntries(keys.map((k) => [k, 0])),
        countIn: 0,
        countOut: 0,
      };
    }
    const c = byCurrency[cur];
    if (dir === "inbound") {
      c.in[key] += amt;
      c.countIn += 1;
    } else {
      c.out[key] += amt;
      c.countOut += 1;
    }
    const h = item.held || {};
    if (h.amount || h.paid_at || h.direction || h.currency) held += 1;
  }
  return { days, keys, byCurrency, unreadable, held, outside };
}

function Chart({ days, keys, c, currency }) {
  const series = keys.map((k, i) => ({ key: k, date: days[i], in: c.in[k], out: c.out[k] }));
  const max = Math.max(1, ...series.map((s) => Math.max(s.in, s.out)));
  const H = 96;
  const bar = (v) => (v > 0 ? Math.max(2, Math.round((v / max) * H)) : 1);
  const label =
    `Daily money in and out in ${currency}, ${shortDate(days[0])} to ${shortDate(days[days.length - 1])}. `
    + "Money in is drawn above the line and money out below it. "
    + "Every day is also listed in the table below.";

  return (
    <Box sx={{ overflowX: "auto", minWidth: 0 }}>
      <Box role="img" aria-label={label} sx={{ minWidth: DAYS * 14 }}>
        <Box sx={{ display: "flex", gap: "3px", alignItems: "stretch" }}>
          {series.map((s) => (
            <Tooltip
              key={s.key}
              title={`${shortDate(s.date)} — in ${money(s.in, currency)}, out ${money(s.out, currency)}`}
            >
              <Box tabIndex={0} sx={{ flex: 1, display: "flex", flexDirection: "column", outline: "none" }}>
                <Box sx={{ height: H, display: "flex", alignItems: "flex-end" }}>
                  <Box
                    sx={{
                      width: "100%",
                      height: bar(s.in),
                      bgcolor: s.in > 0 ? "text.primary" : "divider",
                      borderRadius: "2px 2px 0 0",
                    }}
                  />
                </Box>
                <Box sx={{ height: "1px", bgcolor: "text.secondary" }} />
                <Box sx={{ height: H, display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      width: "100%",
                      height: bar(s.out),
                      bgcolor: s.out > 0 ? "text.disabled" : "divider",
                      borderRadius: "0 0 2px 2px",
                    }}
                  />
                </Box>
              </Box>
            </Tooltip>
          ))}
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">{shortDate(days[0])}</Typography>
          <Typography variant="caption" color="text.secondary">{shortDate(days[14])}</Typography>
          <Typography variant="caption" color="text.secondary">Today</Typography>
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
        Above the line: money in. Below the line: money out. Bars are relative to the
        largest single day ({money(max, currency)}), not to a printed scale. A hairline
        means nothing was paid that day.
      </Typography>
    </Box>
  );
}

export default function App({ token, claims, onSignOut }) {
  const [state, setState] = useState(null);
  const [picked, setPicked] = useState(null);
  const get = useMemo(() => kernel(token), [token]);

  const load = useCallback(async () => {
    setState(null);
    try {
      const schema = await get("/schema");
      const t = (schema.types || []).find((x) => x.type === "Payment");
      if (!t || !t.collection) {
        setState({ missing: ["the Payment type"] });
        return;
      }
      const names = new Set((t.fields || []).map((f) => f.name));
      const missing = NEEDED.filter((n) => !names.has(n));
      if (missing.length) {
        setState({ missing });
        return;
      }
      let items = [];
      let cursor = null;
      let more = false;
      let pages = 0;
      let first = null;
      do {
        const params = { limit: PAGE };
        if (cursor) params.cursor = cursor;
        const body = await get(`/${t.collection}`, params);
        if (!first) first = body;
        items = items.concat(body.items || []);
        more = !!body.has_more;
        cursor = body.cursor;
        pages += 1;
      } while (more && cursor && pages < MAX_PAGES);
      setState({
        items,
        truncated: more,
        permitted: first?.permitted_tags || [],
        declared: first?.declared_tags || null,
        readAt: new Date(),
      });
    } catch (e) {
      setState({ failure: e });
    }
  }, [get]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(
    () => (state?.items ? summarise(state.items, state.readAt) : null),
    [state],
  );

  const currencies = summary
    ? Object.keys(summary.byCurrency).sort(
      (a, b) =>
        summary.byCurrency[b].countIn + summary.byCurrency[b].countOut
        - (summary.byCurrency[a].countIn + summary.byCurrency[a].countOut),
    )
    : [];
  const currency = picked && currencies.includes(picked) ? picked : currencies[0];
  const c = currency ? summary.byCurrency[currency] : null;
  const totalIn = c ? Object.values(c.in).reduce((a, b) => a + b, 0) : 0;
  const totalOut = c ? Object.values(c.out).reduce((a, b) => a + b, 0) : 0;
  const net = totalIn - totalOut;

  const header = (
    <Stack
      direction="row"
      spacing={2}
      sx={{ alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap" }}
    >
      <Typography variant="h1">Cash Flow · 30 days</Typography>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        <Typography variant="body2" color="text.secondary">
          {claims?.email || claims?.username || ""}
        </Typography>
        <Typography
          component="button"
          variant="body2"
          onClick={onSignOut}
          sx={{ background: "none", border: 0, cursor: "pointer", color: "primary.main" }}
        >
          Sign out
        </Typography>
      </Stack>
    </Stack>
  );

  const strip = (
    <Permission
      gate={
        "Payments this app's reviewed manifest lets it read, intersected with your own "
        + "groups. The app reads Payment records and nothing else, and changes nothing."
      }
      groups={claims?.["cognito:groups"] || []}
      effective={{ permitted: state?.permitted || [], declared: state?.declared || null }}
    />
  );

  let body;
  if (!state) {
    body = <Loading label="Reading payments from the kernel" minHeight="40vh" />;
  } else if (state.failure) {
    body = <Refusal failure={state.failure} actor={ACTOR} onRetry={load} />;
  } else if (state.missing) {
    body = (
      <Alert severity="warning" variant="outlined">
        <AlertTitle>The kernel's schema no longer has what this chart needs</AlertTitle>
        Missing: {state.missing.join(", ")}. Nothing is drawn rather than drawing zeros.
      </Alert>
    );
  } else if (!c) {
    body = (
      <EmptyState title="No payments you may see in the last 30 days">
        <Typography variant="body2">
          The kernel answered and this app was allowed to ask. It returned{" "}
          {state.items.length} payment{state.items.length === 1 ? "" : "s"} in total, none
          dated in the last 30 days. That means none reached you, not that no money moved.
        </Typography>
      </EmptyState>
    );
  } else {
    const rows = summary.keys.map((k, i) => ({ k, d: summary.days[i], in: c.in[k], out: c.out[k] }));
    body = (
      <Stack spacing={3} sx={{ minWidth: 0 }}>
        {state.truncated ? (
          <Alert severity="warning" variant="outlined">
            <AlertTitle>Not every payment was read</AlertTitle>
            This screen stopped after {state.items.length} payments and the kernel said there
            were more. The totals below may be low.
          </Alert>
        ) : null}

        {currencies.length > 1 ? (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
            <Typography variant="body2" color="text.secondary">
              Currencies are never added together:
            </Typography>
            {currencies.map((cur) => (
              <Chip
                key={cur}
                label={cur}
                size="small"
                variant={cur === currency ? "filled" : "outlined"}
                onClick={() => setPicked(cur)}
                aria-pressed={cur === currency}
              />
            ))}
          </Stack>
        ) : null}

        <Box
          sx={{ borderLeft: 2, borderColor: "primary.main", pl: 2 }}
          title={`Sum of inbound minus sum of outbound Payment records dated ${shortDate(summary.days[0])} to today, in ${currency}, as read at ${state.readAt.toLocaleTimeString()}.`}
        >
          <Typography sx={{ fontSize: { xs: "2.25rem", sm: "3rem" }, fontWeight: 300, lineHeight: 1.1 }}>
            {net < 0 ? "−" : net > 0 ? "+" : ""}
            {money(Math.abs(net), currency)}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            net · money in minus money out, last 30 days ({net < 0 ? "more went out than came in" : net > 0 ? "more came in than went out" : "even"})
          </Typography>
        </Box>

        <Stack direction="row" spacing={4} sx={{ flexWrap: "wrap", rowGap: 2 }}>
          <Box>
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 300 }}>{money(totalIn, currency)}</Typography>
            <Typography variant="caption" color="text.secondary">
              money in · {c.countIn} payment{c.countIn === 1 ? "" : "s"} received
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 300 }}>{money(totalOut, currency)}</Typography>
            <Typography variant="caption" color="text.secondary">
              money out · {c.countOut} payment{c.countOut === 1 ? "" : "s"} made
            </Typography>
          </Box>
        </Stack>

        <Chart days={summary.days} keys={summary.keys} c={c} currency={currency} />

        <Disclose label={`Every day, as a table (${DAYS} rows)`}>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Day</TableCell>
                  <TableCell align="right">Money in</TableCell>
                  <TableCell align="right">Money out</TableCell>
                  <TableCell align="right">Net</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.slice().reverse().map((r) => (
                  <TableRow key={r.k}>
                    <TableCell>{r.d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</TableCell>
                    <TableCell align="right">{money(r.in, currency)}</TableCell>
                    <TableCell align="right">{money(r.out, currency)}</TableCell>
                    <TableCell align="right">{money(r.in - r.out, currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Disclose>

        <Disclose label="Where these numbers come from">
          <Stack spacing={1.5} sx={{ maxWidth: "62ch" }}>
            <Typography variant="body2" color="text.secondary">
              Payment records the kernel returned to you through this app — money that
              actually moved, not invoices. Each is placed on the day of its paid date in
              your browser's time zone. Every payment is counted whatever its status
              says, because that status is the source's own wording and is not mapped.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              These are the payments you can see now. A payment you are not permitted to
              see is not in these totals, and one synced later will change them.
              Read {state.items.length} payment{state.items.length === 1 ? "" : "s"}; {summary.outside} dated outside the window.
            </Typography>
            {summary.unreadable ? (
              <Typography variant="body2" color="text.secondary">
                {summary.unreadable} payment{summary.unreadable === 1 ? " was" : "s were"} left out
                because a date, amount, direction or currency could not be read.
              </Typography>
            ) : null}
            {summary.held ? (
              <Typography variant="body2" color="text.secondary">
                {summary.held} payment{summary.held === 1 ? " has" : "s have"} a value set by a person
                in this org over what the source reported; those values are the ones counted.
              </Typography>
            ) : null}
          </Stack>
        </Disclose>
      </Stack>
    );
  }

  return (
    <Stack spacing={3} sx={{ p: { xs: 2, sm: 3 }, minWidth: 0, maxWidth: 1100 }}>
      {header}
      {strip}
      {body}
    </Stack>
  );
}
